import type { Id } from '@/convex/_generated/dataModel';
import type { OrgChatAttachmentInput } from '@/components/org-chat/org-chat-types';
import {
  type OrgChatSendPayload,
  isCustomReferenceAttachment,
} from './org-chat-api-attachment';
import {
  createOrgChatClientInputId,
  uniqueStrings,
  isWorkspaceNativeArtifactReference,
  getStablePreviewUrl,
} from './attachment-identity-keys';
export const buildBaseMessagePayload = ({
  orgId,
  prompt,
  rawUserPrompt,
  forceDeepResearch,
}: {
  orgId: Id<'organizations'>;
  prompt: string;
  rawUserPrompt?: string;
  forceDeepResearch?: boolean;
}): Pick<
  OrgChatSendPayload,
  | 'orgId'
  | 'prompt'
  | 'rawUserPrompt'
  | 'forceDeepResearch'
  | 'deliveryMetadata'
> => ({
  orgId,
  prompt,
  ...(typeof rawUserPrompt === 'string' ? { rawUserPrompt } : {}),
  ...(forceDeepResearch ? { forceDeepResearch: true } : {}),
  deliveryMetadata: {
    deliveryKind: 'send',
    clientInputId: createOrgChatClientInputId(),
    createdAt: Date.now(),
  },
});

export const buildBasePayload = ({
  orgThreadId,
  ...message
}: Parameters<typeof buildBaseMessagePayload>[0] & {
  orgThreadId: Id<'orgThreads'>;
}) => ({ orgThreadId, ...buildBaseMessagePayload(message) });

export const buildAttachmentFields = (
  attachments?: Array<OrgChatAttachmentInput>,
  /** Files still uploading that the send takes along (#4844); no stored object yet. */
  pendingUploadIds?: ReadonlyArray<string>
):
  | Pick<
      OrgChatSendPayload,
      'attachments' | 'referenceVfsPaths' | 'selectedContextWorkspaceFileIds'
    >
  | Record<string, never> => {
  // Thread mutations validate attachments against this transport shape. Keep
  // chip identity and preview fields so the persisted user message can render
  // the same inline chip the composer showed before send.
  const genericWorkspaceAttachments = (attachments ?? []).filter(
    (attachment) => !isCustomReferenceAttachment(attachment)
  );
  const workspacePathAttachments = genericWorkspaceAttachments.filter(
    (attachment) => Boolean(attachment.vfsPath)
  );
  const workspaceReferenceVfsPaths = uniqueStrings(
    workspacePathAttachments.map((attachment) => attachment.vfsPath)
  );
  const workspaceFileIds = Array.from(
    new Set(
      genericWorkspaceAttachments
        .map((attachment) => attachment.workspaceFileId)
        .filter((id): id is Id<'workspaceFiles'> => Boolean(id))
    )
  );

  const actionAttachments = (attachments ?? [])
    .filter(
      (attachment) =>
        !attachment.vfsPath ||
        !(
          isWorkspaceNativeArtifactReference(attachment.vfsPath) ||
          isWorkspaceNativeArtifactReference(attachment.filename)
        )
    )
    .filter((attachment) =>
      Boolean(
        attachment.storageKey ||
          attachment.storageId ||
          attachment.url ||
          attachment.previewUrl ||
          attachment.workspaceFileId ||
          attachment.vfsPath ||
          (attachment.uploadId &&
            pendingUploadIds?.includes(attachment.uploadId))
      )
    )
    .map((attachment) => {
      const previewUrl = getStablePreviewUrl(attachment.previewUrl);
      const resolvedUrl = attachment.url || previewUrl;
      return {
        kind: attachment.kind,
        filename: attachment.filename,
        mimeType: attachment.mimeType,
        sizeBytes: attachment.sizeBytes,
        ...(attachment.storageKey ? { storageKey: attachment.storageKey } : {}),
        ...(resolvedUrl ? { url: resolvedUrl } : {}),
        ...(attachment.workspaceFileId
          ? { workspaceFileId: attachment.workspaceFileId }
          : {}),
        ...(attachment.vfsPath ? { vfsPath: attachment.vfsPath } : {}),
        ...(previewUrl ? { previewUrl } : {}),
        ...(attachment.storageId ? { storageId: attachment.storageId } : {}),
        ...(attachment.uploadId ? { uploadId: attachment.uploadId } : {}),
        ...(attachment.source ? { source: attachment.source } : {}),
        ...(attachment.referenceCategory
          ? { referenceCategory: attachment.referenceCategory }
          : {}),
        ...(attachment.customReferenceCategoryId
          ? {
              customReferenceCategoryId: attachment.customReferenceCategoryId,
            }
          : {}),
      };
    });

  const fields: Pick<
    OrgChatSendPayload,
    'attachments' | 'referenceVfsPaths' | 'selectedContextWorkspaceFileIds'
  > = {};
  if (actionAttachments.length > 0) {
    fields.attachments = actionAttachments;
  }
  if (workspaceReferenceVfsPaths.length > 0) {
    fields.referenceVfsPaths = workspaceReferenceVfsPaths;
  }
  if (workspaceFileIds.length > 0) {
    fields.selectedContextWorkspaceFileIds = workspaceFileIds;
  }

  return Object.keys(fields).length > 0 ? fields : {};
};
